import type { ToolContent } from "@/types/tools";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import HeroBlock from "../shared/hero-block";
import ToolButton from "./ToolButton";

const HERO_COMPONENTS: Record<string, React.ComponentType<{ content: ToolContent }>> = {
  "audio-balancer": dynamic(() => import("./hero-components/AudioBalancer")),
  "video-compressor": dynamic(() => import("./hero-components/VideoCompressor")),
  "mp3-converter": dynamic(() => import("./hero-components/MP3Converter")),
};

interface ToolHeroProps {
  toolContent: ToolContent;
}

const ToolHero = ({ toolContent }: ToolHeroProps) => {
  const HeroComp = useMemo(() => {
    return (
      HERO_COMPONENTS[toolContent.heroComponentKey as string] ??
      (({ content }: { content: ToolContent }) => null)
    );
  }, [toolContent.heroComponentKey]);

  return (
    <HeroBlock wrapperStyle="items-center gap-6 sm:gap-8 py-14 lg:py-14">
      <div className="flex w-full flex-col items-center gap-3 text-center md:gap-4">
        <h1 className="text-4xl font-semibold leading-[1.25] text-gray-900 sm:text-4xl sm:leading-[1.25] md:text-5xl md:leading-[1.25] lg:text-6xl lg:leading-[1.25]">
          {toolContent.heroTitlePrefix}
          <br /> {toolContent.heroTitleSuffix}
        </h1>
        <p className="flex max-w-[70%] items-center text-base text-gray-500 max-sm:max-w-full  sm:text-lg md:text-xl">
          {toolContent.heroDescription}
        </p>
      </div>
      {toolContent.heroComponentKey && toolContent.heroComponentPos === "middle" && (
        <div className="relative flex w-full items-center justify-center">
          <HeroComp content={toolContent} />
        </div>
      )}
      <div className="flex w-full flex-col items-center gap-3 md:gap-4">
        <ToolButton buttonText={toolContent.buttonText} buttonLink={toolContent.buttonLink} />
        <p className="text-base text-gray-500 sm:text-lg">
          No signup required. <span className="text-blue-500">100% free!</span>
        </p>
      </div>
      {toolContent.heroComponentKey && toolContent.heroComponentPos === "bottom" && (
        <div className="relative flex w-full items-center justify-center">
          <HeroComp content={toolContent} />
        </div>
      )}
    </HeroBlock>
  );
};

export default ToolHero;
